<template>
  <v-container>
    <avs-backButton
      v-if="!inDialog"
      backTo="Reservations"
    />

    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template #headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.informations]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.informations">
            {{ $t("commons.validation_issues") }}
          </small>
        </AvsStep>
      </template>

      <template #items>
        <v-stepper-content step="1">
          <v-form
            ref="form-informations"
            v-model="isFormValid.informations"
            lazy-validation
          >
            <v-row>
              <v-col md="12">
                <v-text-field
                  v-model="staff.title"
                  :rules="rules.title"
                  :label="$t('Titre')"
                  dense
                  outlined
                />
              </v-col>
            </v-row>
            <!-- Dates -->
            <v-row>
              <v-col md="6">
                <DatetimePicker
                  v-model="staff.start_date"
                  :textFieldProps="{
                    placeholder: $t('commons.date.start_date'),
                    rules: rules.start_date
                  }"
                  :label="$t('commons.date.start_date')"
                />
              </v-col>
              <v-col md="6">
                <DatetimePicker
                  v-model="staff.end_date"
                  :min="staff.start_date"
                  :textFieldProps="{
                    placeholder: $t('commons.date.end_date'),
                    rules: rules.end_date
                  }"
                  :label="$t('commons.date.end_date')"
                />
              </v-col>
            </v-row>

            <!-- Client + Espace -->
            <v-row>
              <v-col md="6">
                <v-autocomplete
                  v-model="staff.customer"
                  :items="customers"
                  :filter="filterCustomers"
                  :search-input.sync="customerSearch"
                  return-object
                  :label="$t('Client')"
                  dense
                  outlined
                  clearable
                >
                  <template #item="{ item }">
                    <div>
                      <strong>{{ item.first_name }} {{ item.last_name }}</strong
                      ><br />
                      <small>{{ item.phone_number }}</small>
                    </div>
                  </template>
                  <template #selection="{ item }">
                    {{ item.first_name }} {{ item.last_name }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col md="6">
                <v-autocomplete
                  v-model="staff.space"
                  :items="spaces"
                  :filter="filterSpaces"
                  :search-input.sync="spaceSearch"
                  return-object
                  :label="$t('Espace/Salle')"
                  dense
                  outlined
                  clearable
                >
                  <template #item="{ item }">
                    <div>{{ item.name }} — {{ formatAmount(item.price) }} FCFA</div>
                  </template>
                  <template #selection="{ item }">
                    {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Statut + Type de remise -->
            <v-row>
              <v-col md="6">
                <StatusInput
                  v-model="staff.status"
                  :rules="rules.status"
                  :items="reservationStatusItems"
                />
              </v-col>
              <v-col md="6">
                <v-select
                  v-model="staff.discount_type"
                  :items="discountTypes"
                  item-text="text"
                  item-value="value"
                  :rules="rules.discount_type"
                  :label="$t('Type de remise')"
                  dense
                  outlined
                />
              </v-col>
            </v-row>

            <!-- Valeur remise + Montant final -->
            <v-row>
              <v-col md="6">
                <v-text-field
                  v-model.number="staff.discount_value"
                  :rules="rules.discount_value"
                  :label="$t('Valeur de la réduction')"
                  type="number"
                  dense
                  outlined
                />
              </v-col>
              <v-col md="6">
                <v-text-field
                  :value="computedFinalPrice"
                  :label="$t('Montant Final')"
                  readonly
                  dense
                  outlined
                />
              </v-col>
            </v-row>

            <!-- Actions -->
            <v-card-actions class="justify-end">
              <avs-saveButton @click="save" />
            </v-card-actions>
          </v-form>
        </v-stepper-content>
      </template>
    </AvsSteppers>

    <avs-CloseDialog
      v-model="closeDialogIsOpen"
      @cancel="handleCancel"
      @confirm="handleConfirm"
    />
  </v-container>
</template>

<script>
import _ from "lodash"
import { ReservationService, CustomerService, SpaceService } from "@/services"
import { mkReservation } from "@/utils/datamodels"
import { required } from "@/utils/validator"
import { RESERVATION_STATUS_ITEMS } from "@/utils/consts"

export default {
  name: "ReservationCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    StatusInput: () => import("@/components/inputs/StatusInput.vue")
  },
  props: {
    inDialog: { type: Boolean, default: false },
    inDialogParams: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      staffRef: null,
      customers: [],
      spaces: [],
      staff: mkReservation(),
      customerSearch: "",
      spaceSearch: "",
      tab: this.$route.params.tab || 1,
      loading: false,
      discountTypes: [
        { text: this.$t("Pourcentage"), value: "percent" },
        { text: this.$t("Montant fixe"), value: "amount" }
      ],
      reservationStatusItems: RESERVATION_STATUS_ITEMS,
      rules: {
        start_date: [required],
        end_date: [required],
        status: [required],
        customer: [required],
        title: [required],
        space: [required],
        discount_type: [required],
        discount_value: [required]
      },
      isFormValid: { informations: false },
      closeDialogIsOpen: false
    }
  },
  async created() {
    await this.fetchCustomers()
    await this.fetchSpaceTypes()
    if (this.id) {
      await this.fetchReservation()
    }
  },
  methods: {
    filterCustomers(item, query) {
      return `${item.first_name} ${item.last_name} ${item.phone_number}`
        .toLowerCase()
        .includes((query || "").toLowerCase())
    },
    filterSpaces(item, query) {
      return `${item.name} ${item.price}`.toLowerCase().includes((query || "").toLowerCase())
    },
    async fetchCustomers() {
      const resp = await CustomerService.get(this, this.project.id)
      this.customers = resp.results
    },
    async fetchSpaceTypes() {
      const spaces = await SpaceService.getAll(this, this.project.id, { excludeArchived: true })
      // Garde-fou : on exclut aussi les archivés côté client
      this.spaces = (spaces || []).filter(s => !s.archived)
    },
    formatAmount(value) {
      // Montant entier avec séparateur de milliers (ex. 1 500 000)
      if (value == null || value === "") return "0"
      const rounded = Math.round(Number(value))
      return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },
    async fetchReservation() {
      const r = await ReservationService.get(this, null, this.id)
      this.staff = { ...mkReservation(), ...r }

      // mapper l'ID vers l'objet complet
      this.staff.space =
        this.spaces.find(s => s.id === r.space || s.id === r.space_info?.id) || null
      this.staff.customer =
        this.customers.find(c => c.id === r.customer || c.id === r.customer_info?.id) || null

      this.staff.discount_type = r.discount_type
      this.staff.discount_value = r.discount_value

      this.staffRef = _.cloneDeep(this.staff)
    },
    async save() {
      this.$refs["form-informations"].validate()
      if (!this.isFormValid.informations) {
        this.tab = 1
        return this.send(this.$t("messages.warning.all_required_info"), "warning")
      }
      this.loading = true
      if (this.staffExists) {
        await this.updateReservation()
      } else {
        await this.createReservation()
      }
      this.loading = false
    },
    async createReservation() {
      const base = _.omit(this.staff, ["space", "customer"])
      const payload = {
        ...base,
        space: this.staff.space.id,
        customer: this.staff.customer.id
      }
      const resp = await ReservationService.create(this, this.project.id, payload)
      this.staff = resp
      this.staffRef = _.cloneDeep(resp)
      this.send(this.$t("messages.success.saved", { entity: "reservation" }), "success")
      this.$router.push({ name: "Reservations" })
    },
    async updateReservation() {
      const base = _.omit(this.staff, ["space", "customer"])
      const payload = {
        ...base,
        space: this.staff.space.id,
        customer: this.staff.customer.id
      }
      if (!_.isEqual(payload, _.omit(this.staffRef, ["space", "customer"]))) {
        await ReservationService.update(this, this.staff.id, payload)
        this.send(
          this.$t("messages.success.updated", {
            entity: this.$tc("Reservation")
          }),
          "success"
        )
      } else {
        this.send(this.$t("messages.success.no_updated"), "info")
      }
      this.$router.push({ name: "Reservations" })
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    handleCancel() {
      this.closeDialogIsOpen = false
    },
    handleConfirm() {
      ReservationService.close(this, this.staff.id).then(() => {
        this.send(
          this.$t("messages.success.closed", {
            entity: this.$tc("categories.reservation.name", 1)
          }),
          "success"
        )
        this.$router.push({ name: "Reservations" })
      })
      this.closeDialogIsOpen = false
    }
  },
  computed: {
    id() {
      return this.inDialog ? this.inDialogParams.id : this.$route.params.id || null
    },
    project() {
      return this.$store.state.app.project
    },
    staffExists() {
      return !!this.staff.id
    },
    computedFinalPrice() {
      if (!this.staff.space || this.staff.discount_value == null) return ""
      const base = parseFloat(this.staff.space.price)
      let final = base
      if (this.staff.discount_type === "percent") {
        final = base * (1 - this.staff.discount_value / 100)
      } else if (this.staff.discount_type === "amount") {
        final = base - this.staff.discount_value
      }
      return final.toFixed(2)
    }
  }
}
</script>

<style lang="scss">
.custom-container {
  display: flex;
  flex-direction: column;
  height: 80vh;
}
.stepper-item {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
